"use client";

import React, { useEffect, useState } from "react";
import type { Coupon } from "@/src/types/tenant";

const DEFAULT_OFFERS = [
  {
    id: "free",
    emoji: "🚚",
    bold: "FREE delivery",
    body: "on orders above ₹499",
    code: null as string | null,
  },
  {
    id: "hdfc",
    emoji: "💳",
    bold: "10% off",
    body: "with HDFC Bank credit cards · Up to ₹250",
    code: "HDFC10",
  },
  {
    id: "new",
    emoji: "🎁",
    bold: "₹100 off",
    body: "on your first GrabLocal order",
    code: "WELCOME100",
  },
];

interface TopOfferBannerProps {
  coupons?: Coupon[];
}

export const TopOfferBanner: React.FC<TopOfferBannerProps> = ({ coupons = [] }) => {
  const offers =
    coupons.length > 0
      ? coupons.map((c) => ({
          id: String(c.id),
          emoji: "🏷️",
          bold: c.code ?? c.name,
          body: `· ${c.name} · ₹${Number(c.amount).toLocaleString("en-IN")} off`,
          code: c.code ?? null,
        }))
      : DEFAULT_OFFERS;

  const [idx, setIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (offers.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % offers.length);
      setAnimKey((k) => k + 1);
    }, 3500);
    return () => clearInterval(t);
  }, [offers.length]);

  if (dismissed) return null;

  const offer = offers[idx];

  return (
    <div
      className="relative flex items-center overflow-hidden"
      style={{
        background: "var(--techno-black)",
        color: "var(--rev-white)",
        fontSize: "12.5px",
        fontWeight: 500,
        height: "38px",
      }}
    >
      {/* Message track */}
      <div
        className="flex flex-1 items-center justify-center text-center"
        style={{ padding: "0 48px" }}
      >
        <div
          key={animKey}
          className="flex items-center gap-2.5"
          style={{ animation: "announceIn 480ms cubic-bezier(.22,1,.36,1)" }}
        >
          <span>{offer.emoji}</span>
          <span>
            <strong style={{ color: "var(--turmeric)", fontWeight: 700 }}>
              {offer.bold}
            </strong>{" "}
            {offer.body}
          </span>
          {offer.code && (
            <>
              <span
                className="rounded-full"
                style={{
                  width: 4,
                  height: 4,
                  background: "rgba(232,234,226,0.4)",
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span>
                Use code{" "}
                <strong style={{ color: "var(--turmeric)", fontWeight: 700 }}>
                  {offer.code}
                </strong>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss announcement"
        className="absolute right-3 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded opacity-70 transition hover:opacity-100"
        style={{ background: "rgba(255,255,255,0.08)", color: "var(--rev-white)" }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};
